import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { isToday } from "date-fns";
import { ASS_STATUS } from "../../../common/constants/status";
import { formatDate } from "../../../utils/date";
import SubmissionLinksDialog from "./SubmissionLinksDialog";

const AssignmentTable = ({
  agendas,
  onRowInteraction,
  onSubmit,
  userStatus,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const handleRowClick = (note, event) => {
    if (!note) return;

    // 모바일에서는 클릭, 데스크톱에서는 더블클릭
    if (isMobile) {
      onRowInteraction(note);
    }
  };

  return (
    <>
    <SubmissionLinksDialog
      open={Boolean(selectedSubmission)}
      onClose={() => setSelectedSubmission(null)}
      submission={selectedSubmission}
    />
    <TableContainer
      component={Paper}
      sx={{
        overflowX: "auto",
        "& .MuiTable-root": {
          minWidth: { xs: "100%", sm: "800px" },
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                minWidth: { xs: "80px", sm: "100px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              마감 날짜
            </TableCell>
            <TableCell
              sx={{
                minWidth: { xs: "100px", sm: "150px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              강의
            </TableCell>
            <TableCell
              sx={{
                minWidth: { xs: "100px", sm: "150px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              과제
            </TableCell>
            <TableCell
              sx={{
                minWidth: { xs: "60px", sm: "80px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              난이도
            </TableCell>
            <TableCell
              sx={{
                minWidth: { xs: "80px", sm: "100px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              제출 상태
            </TableCell>
            <TableCell
              sx={{
                minWidth: { xs: "80px", sm: "100px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
              align="center"
            >
              제출하기
            </TableCell>
            <TableCell
              sx={{
                minWidth: { xs: "120px", sm: "200px" },
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              피드백
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agendas.map((agenda) => {
            const isRowToday = isToday(new Date(agenda.closeDate));
            const rowStyle = {
              backgroundColor: agenda.isClosed
                ? "#e0e0e0"
                : isRowToday
                ? "#ffff00"
                : "inherit",
              "&:last-child td, &:last-child th": { border: 0 },
              cursor: agenda.note ? "pointer" : "default",
              "&:hover": {
                backgroundColor: "rgba(156, 39, 176, 0.1)",
              },
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              userSelect: "none",
            };

            return (
              <TableRow
                key={agenda._id}
                sx={rowStyle}
                onClick={(e) => handleRowClick(agenda.note, e)}
                onDoubleClick={
                  !isMobile ? () => onRowInteraction(agenda.note) : undefined
                }
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                    fontSize: { xs: "0.8rem", sm: "1rem" },
                  }}
                >
                  {formatDate(agenda.closeDate)}
                </TableCell>
                <TableCell
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                    fontSize: { xs: "0.8rem", sm: "1rem" },
                  }}
                >
                  {agenda.lecture}
                </TableCell>
                <TableCell
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                    fontSize: { xs: "0.8rem", sm: "1rem" },
                  }}
                >
                  {agenda.assignment}
                </TableCell>
                <TableCell
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                  }}
                >
                  <Chip
                    label={agenda.level || "-"}
                    color={
                      agenda.level === "상"
                        ? "error"
                        : agenda.level === "중"
                        ? "success"
                        : agenda.level === "하"
                        ? "primary"
                        : "default"
                    }
                    sx={{
                      "& .MuiChip-label": {
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        padding: { xs: "0 8px", sm: "0 12px" },
                      },
                      height: { xs: "24px", sm: "32px" },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                  }}
                >
                  {agenda.submission.length === 0 ? (
                    <span style={{ fontSize: isMobile ? "0.8rem" : "1rem" }}>
                      미제출
                    </span>
                  ) : (
                    <Chip
                      label={
                        ASS_STATUS[
                          agenda.submission[agenda.submission.length - 1].state
                        ].message
                      }
                      color={
                        ASS_STATUS[
                          agenda.submission[agenda.submission.length - 1].state
                        ].color
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSubmission(
                          agenda.submission[agenda.submission.length - 1]
                        );
                      }}
                      sx={{
                        cursor: "pointer",
                        "& .MuiChip-label": {
                          fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          padding: { xs: "0 8px", sm: "0 12px" },
                        },
                        height: { xs: "24px", sm: "32px" },
                      }}
                    />
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                  }}
                >
                  {agenda.submissionLinks.length === 0 ? (
                    <span style={{ fontSize: isMobile ? "0.8rem" : "1rem" }}>
                      과제 없음
                    </span>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={agenda.isClosed || userStatus !== "active"}
                      onClick={() => onSubmit(agenda)}
                      sx={{
                        whiteSpace: "nowrap",
                        minWidth: { xs: "60px", sm: "80px" },
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        padding: { xs: "4px 8px", sm: "6px 16px" },
                      }}
                    >
                      {agenda.isClosed ? "마감" : "제출하기"}
                    </Button>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    padding: { xs: "8px 4px", sm: "16px" },
                    fontSize: { xs: "0.8rem", sm: "1rem" },
                  }}
                >
                  {agenda.submission.length > 0
                    ? agenda.submission[agenda.submission.length - 1].feedback
                    : ""}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
};

export default AssignmentTable;
